/**
 * Timezone utility for consistent EST (America/New_York) display
 * All dates/times in the app should use these functions for display
 */

const EST_TIMEZONE = 'America/New_York';

/**
 * Format date to EST timezone
 * @param {string|Date} date - Date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in EST
 */
export const formatDateEST = (date, options = {}) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  const defaultOptions = {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
};

/**
 * Format date and time to EST timezone
 * @param {string|Date} date - Date string or Date object
 * @param {boolean} includeSeconds - Include seconds in output
 * @returns {string} Formatted date+time string in EST
 */
export const formatDateTimeEST = (date, includeSeconds = false) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  const options = {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  
  if (includeSeconds) {
    options.second = '2-digit';
  }
  
  return new Intl.DateTimeFormat('en-US', options).format(dateObj) + ' EST';
};

/**
 * Format time only to EST timezone
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted time string in EST
 */
export const formatTimeEST = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return 'Invalid Time';
  
  const options = {
    timeZone: EST_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  
  return new Intl.DateTimeFormat('en-US', options).format(dateObj) + ' EST';
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTimeEST = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  const now = new Date();
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  
  return formatDateEST(dateObj);
};

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

/**
 * Get current date/time in EST
 * @returns {Date} Current date in EST
 */
export const getCurrentDateEST = () => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: EST_TIMEZONE }));
};

/**
 * Check if a date is today in EST
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is today in EST
 */
export const isTodayEST = (date) => {
  if (!date) return false;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const today = getCurrentDateEST();
  
  return dateObj.toLocaleDateString('en-US', { timeZone: EST_TIMEZONE }) === 
         today.toLocaleDateString('en-US', { timeZone: EST_TIMEZONE });
};

/**
 * Format date for input field (YYYY-MM-DD in EST)
 * @param {string|Date} date - Date to format
 * @returns {string} Date string for input
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  // Get date parts in EST
  const options = { timeZone: EST_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' };
  const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(dateObj);
  
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;
  
  return `${year}-${month}-${day}`;
};
